import { DropdownButtonType, List, ListItem, SingleSelectDropdown, TextLinkWidget } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';

import { Messages } from '../../../../../codegen/Localize';
import { ITimeZone } from '../../../../../userSettings/serverApi/types/ITimeZone';
import { IGetTimeZonesResult, TimeZones } from '../../../../../userSettings/services/TimeZones';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { dropdownListStyle, learnMoreStyle } from '../../../../styles/CommonStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export const SITE_TIME_ZONE_SECTION_TEST_ID = 'default-site-time-zone-site-setting';
export const SITE_TIME_ZONE_LEARN_MORE_TEST_ID = 'site-time-zone-learn-more';
export const SITE_TIME_ZONE_DROPDOWN_TEST_ID = 'site-time-zone-dropdown';
export const unspecifiedTimeZone = 'UnspecifiedTimeZone';

export type SiteTimeZoneSectionProps = SiteSettingsForSiteAdminProps<'timeZone' | 'useDefaultTimeZone'> & { timeZonesListItems?: ListItem[] };

export function SiteTimeZoneSection(props: SiteTimeZoneSectionProps) {
  const siteTimeZoneFFEnabled = FeatureFlags.siteTimeZoneEnabled;
  const formik = useFormikContext<SiteTimeZoneSectionProps>();
  const changeTimeZoneHandler = useChangeTimeZoneHandler(formik);
  const timeZonesListItems = useTimeZonesListItems(siteTimeZoneFFEnabled);

  useEffect(() => {
    if (!formik.initialValues.timeZonesListItems && timeZonesListItems && timeZonesListItems.length > 0) {
      formik.setFieldValue('timeZonesListItems', timeZonesListItems);
      formik.initialValues.timeZonesListItems = timeZonesListItems;
    }
  }, [formik, timeZonesListItems]);

  return !siteTimeZoneFFEnabled ? null : (
    <SettingsSection
      title={Messages.defaultSiteTimeZoneSectionHeader()}
      description={getDescription()}
      testId={SITE_TIME_ZONE_SECTION_TEST_ID}
    >
      <TimeZoneDropdown
        changeTimeZoneHandler={changeTimeZoneHandler}
        settingsValues={formik.values}
        timeZonesListItems={timeZonesListItems}
      />
    </SettingsSection>
  );
}

function getDescription() {
  return <>
    {Messages.defaultSiteTimeZoneSectionText()}
    <TextLinkWidget
      className={learnMoreStyle}
      href={HelpService.getServerHelpUrlByCategory('tz-for-extracts')}
      target='_blank'
      testId={SITE_TIME_ZONE_LEARN_MORE_TEST_ID}
    >
      {Messages.siteTimeZoneSettingLearnMore()}
    </TextLinkWidget>
  </>;
}

/*
 * A custom component is needed to render the time zone options because of the custom mapping:
 *  1. blank option (UnspecifiedTimeZone) -> useDefaultTimeZone=true, timeZone=undefined
 *  2. any other option -> useDefaultTimeZone=false, timeZone=that option's timeZoneId
 */
function useChangeTimeZoneHandler(formik: FormikContextType<SiteTimeZoneSectionProps>) {
  return React.useCallback(
    (selectedTimeZone: string) => {
      if (selectedTimeZone === unspecifiedTimeZone) {
        formik.setFieldValue('settings.useDefaultTimeZone', true);
        formik.setFieldValue('settings.timeZone', undefined);
      } else {
        formik.setFieldValue('settings.useDefaultTimeZone', false);
        formik.setFieldValue('settings.timeZone', selectedTimeZone);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue]);
}

function useTimeZonesListItems(siteTimeZoneFFEnabled: boolean) {
  const [timeZonesListItems, setTimeZonesListItems] = React.useState<ListItem[]>([]);

  // API call to get the list of time zones
  React.useEffect(() => {
    if (siteTimeZoneFFEnabled) {
      let shouldSetState = true; // will be false when component unmounts to prevent memory leak
      TimeZones.getAll().then((result: IGetTimeZonesResult) => {
        // adding a blank option for when the site is using the default time zone
        const blankOption: ITimeZone = {
          displayName: ' ',
          timeZoneId: unspecifiedTimeZone,
          timeZoneRawOffsetMinutes: 0
        };
        if (shouldSetState) {
          // Hyper does not support extra time zones, so we use only curated time zones.
          setTimeZonesListItems(
            [blankOption].concat(result.curatedTimeZonesList).map((timeZone: ITimeZone) => ({
              content: timeZone.displayName,
              value: timeZone.timeZoneId,
              testId: timeZone.timeZoneId
            }))
          );
        }
      });

      return () => {
        shouldSetState = false;
      };
    }
  }, [siteTimeZoneFFEnabled]);

  return timeZonesListItems;
}

type TimeZoneDropdownProps = {
  changeTimeZoneHandler: (selectedTimeZone: string) => void;
  settingsValues: SiteTimeZoneSectionProps;
  timeZonesListItems: ListItem[];
};

function TimeZoneDropdown(props: TimeZoneDropdownProps) {
  const currentTimeZone = props.settingsValues.settings.useDefaultTimeZone ? unspecifiedTimeZone : props.settingsValues.settings.timeZone;
  return (
    <SingleSelectDropdown
      list={<List items={props.timeZonesListItems} className={dropdownListStyle} />}
      value={currentTimeZone}
      onChange={props.changeTimeZoneHandler}
      type={DropdownButtonType.Outline}
      label=''
      aria-label={Messages.defaultSiteTimeZoneSectionText()}
      testId={SITE_TIME_ZONE_DROPDOWN_TEST_ID}
    />
  );
}

/* for registering the component in settings registry */
export const SiteTimeZoneSectionSpecs = componentRegistryEntry({
  name: 'SiteTimeZoneSection',
  component: SiteTimeZoneSection
});
